import React, { useState, useMemo } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import AppHeader, { isAppMode } from './components/AppHeader';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { AuthProvider } from './auth/AuthContext';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from './components/ErrorBoundary';

import Home from './pages/Home';
import SignIn from './auth/SignIn';
import SignUp from './auth/SignUp';
import NewMatch from './pages/NewMatch';
import StaffLogin from './auth/StaffLogin';
import Map from './pages/Map';

import MatchHome from './pages/match/MatchHome';
import MatchShooters from './pages/match/ShootersList';
import MatchSquads from './pages/match/Squads';
import MatchStages from './pages/match/Stages';
import MatchResults from './pages/match/Results';
import ResultsLanding from './pages/match/ResultsLanding';
import MatchRegistration from './pages/match/Registration';
import CompareShooters from './pages/match/CompareShooters';
import ShooterStats from './pages/match/ShooterStats';
import FlagsPodium from './pages/match/FlagsPodium';

import ManageHome from './pages/manage/ManageHome';
import ManageSettings from './pages/manage/Settings';
import ManageStaff from './pages/manage/Staff';
import ManageStages from './pages/manage/Stages';
import ManageSquads from './pages/manage/Squads';
import ManageShooters from './pages/manage/Shooters';
import ManageScoring from './pages/manage/Scoring';
import ManageScoreTable from './pages/manage/ScoreTable';
import ManageAnnouncements from './pages/manage/Announcements';
import ManageTeams from './pages/manage/Teams';
import ManageChrono from './pages/manage/Chrono';
import ManageComments from './pages/manage/Comments';
import ManageStats from './pages/manage/Stats';
import ManageWinMSS from './pages/manage/WinMSS';
import ManageAgent from './pages/manage/Agent';

import RegionHome from './pages/region/RegionHome';
import RegionManage from './pages/region/RegionManage';
import ClubManage from './pages/club/ClubManage';

import LeagueHome from './pages/league/LeagueHome';
import CreateLeague from './pages/league/CreateLeague';

import MatchViewerWrapper from './pages/match/ViewerWrapper';
import MatchManageWrapper from './pages/manage/ManageWrapper';

import AppLayout from './pages/app/AppLayout';
import AppMeMatch from './pages/app/MeMatch';
import AppQuickScoreMatch from './pages/app/QuickScoreMatch';
import AppQuickScoreStage from './pages/app/QuickScoreStage';

function AppModeHeader() {
  const location = useLocation();
  // Don't show on /app routes (AppLayout has its own header)
  // Don't show on /manage routes (ManageWrapper handles its own header/sidebar)
  if (!isAppMode()) return null;
  if (location.pathname.startsWith('/app')) return null;
  if (location.pathname.includes('/manage')) return null;
  return <AppHeader />;
}

function App() {
  const { i18n } = useTranslation();
  const savedLang = localStorage.getItem('userLanguage');
  const [locale, setLocale] = useState(() => {
    if (savedLang) {
      i18n.changeLanguage(savedLang.split('-')[0]);
      return savedLang;
    }
    return i18n.language || 'en';
  });

  const isRtl = locale === 'he' || locale === 'he-IL' || locale === 'iw-IL';

  const theme = useMemo(() => createTheme({
    direction: isRtl ? 'rtl' : 'ltr',
    palette: {
      primary: { main: '#2196f3' },
      secondary: { main: '#f50057' },
      background: { default: 'rgb(238, 238, 238)', paper: '#ffffff' },
    },
    typography: {
      fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    },
  }), [isRtl]);

  const emotionCache = useMemo(() => createCache({
    key: isRtl ? 'muirtl' : 'muiltr',
    stylisPlugins: isRtl ? [prefixer, rtlPlugin] : [prefixer],
  }), [isRtl]);

  React.useEffect(() => {
    document.body.className = isRtl ? 'rtl' : 'ltr';
    document.body.dir = isRtl ? 'rtl' : 'ltr';
  }, [isRtl]);

  // Sync locale state when i18n language changes (e.g. from sidebar language switcher)
  React.useEffect(() => {
    const onLangChanged = (lng) => setLocale(lng);
    i18n.on('languageChanged', onLangChanged);
    return () => i18n.off('languageChanged', onLangChanged);
  }, [i18n]);

  const changeLocale = (newLocale, { explicit = true } = {}) => {
    setLocale(newLocale);
    i18n.changeLanguage(newLocale.split('-')[0]);
    if (explicit) {
      localStorage.setItem('userLanguage', newLocale);
    } else if (!localStorage.getItem('userLanguage')) {
      // First match visit with no saved preference: persist so subsequent
      // matches don't override it
      localStorage.setItem('userLanguage', newLocale);
    }
  };

  return (
    <CacheProvider value={emotionCache}>
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
        <AppModeHeader />
          <Routes>
            {/* Home */}
            <Route path="/" element={<Home locale={locale} onLocaleChange={changeLocale} />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/new" element={<NewMatch />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/staff-login" element={<StaffLogin />} />
            <Route path="/map" element={<Map locale={locale} onLocaleChange={changeLocale} />} />

            {/* Region routes */}
            <Route path="/regions/:regionId" element={<RegionHome locale={locale} />} />
            <Route path="/regions/:regionId/manage/*" element={<RegionManage locale={locale} />} />

            {/* Club routes */}
            <Route path="/clubs/:clubId/manage" element={<ClubManage locale={locale} />} />

            {/* League routes */}
            <Route path="/leagues/new" element={<CreateLeague locale={locale} />} />
            <Route path="/leagues/:leagueId" element={<LeagueHome locale={locale} />} />

            {/* Mobile app routes */}
            <Route path="/app" element={<AppLayout />}>
              <Route index element={null} />
              <Route path="me" element={null} />
              <Route path="me/:matchId" element={<AppMeMatch />} />
              <Route path="quickscore" element={null} />
              <Route path="quickscore/:matchIndex" element={<AppQuickScoreMatch />} />
              <Route path="quickscore/:matchIndex/:stageIndex" element={<AppQuickScoreStage />} />
            </Route>

            {/* Match management routes (independent of viewer wrapper) */}
            <Route path="/:matchId/manage" element={<MatchManageWrapper />}>
              <Route index element={<ManageHome />} />
              <Route path="settings" element={<ManageSettings />} />
              <Route path="staff" element={<ManageStaff />} />
              <Route path="stages" element={<ManageStages />} />
              <Route path="squads" element={<ManageSquads />} />
              <Route path="shooters" element={<ManageShooters />} />

              <Route path="scores/*" element={<ManageScoring />} />
              <Route path="score-table" element={<ManageScoreTable />} />
              <Route path="announcements" element={<ManageAnnouncements />} />
              <Route path="teams" element={<ManageTeams />} />
              <Route path="chrono/*" element={<ManageChrono />} />
              <Route path="stats" element={<ManageStats />} />
              <Route path="winmss" element={<ManageWinMSS />} />
              <Route path="agent" element={<ManageAgent />} />
              <Route path="comments/:shooterId" element={<ManageComments />} />
            </Route>

            {/* Match viewer routes */}
            <Route path="/:matchId" element={<MatchViewerWrapper locale={locale} onLocaleChange={changeLocale} />}>
              <Route index element={<MatchHome />} />
              <Route path="shooters" element={<MatchShooters />} />
              <Route path="squads" element={<MatchSquads />} />
              <Route path="stages" element={<MatchStages />} />
              <Route path="stages/:stageIdx" element={<MatchStages />} />
              <Route path="results" element={<ResultsLanding />} />
              <Route path="registration" element={<MatchRegistration />} />
              <Route path="ro-registration" element={<MatchRegistration ro />} />
              <Route path="shooter-stats" element={<ShooterStats />} />
              <Route path="shooter-stats/:shooterId" element={<ShooterStats />} />
              <Route path="compare-shooters" element={<CompareShooters />} />
              <Route path="score-table" element={<MatchResults />} />
              <Route path="flags-podium" element={<FlagsPodium />} />
            </Route>
          </Routes>
        </ErrorBoundary>
      </ThemeProvider>
    </AuthProvider>
    </CacheProvider>
  );
}

export default App;
